/**
 * SPA route and UI permission keys.
 *
 * - Add a permission to `PERMISSIONS`, map it in `seeders/permissions.seed.ts` (`spaKey`),
 *   then reference it from `ROUTE_RULES` and UI (`<Can permission="…" />` / `can("…")`).
 * - Runtime access uses permissions from `/api/me` via `resolveUserPermissions`.
 */

export const APP_ROLES = ["ADMIN", "VIEWER", "REVIEWER", "AUDITOR"] as const;

export type AppRole = (typeof APP_ROLES)[number];

/** Client permission keys — keep in sync with `spaKey` in `seeders/permissions.seed.ts`. */
export const PERMISSIONS = [
  "view:dashboard",
  "upload:videos",
  "view:history",
  "view:audit-trail",
  "view:users",
  "view:roles",
  "view:alerts",
  "view:screenshots",
  "review:screenshots",
  "review:alerts",
  "view:masters",
  "create:masters",
  "update:masters",
  "delete:masters",
  "view:reports",
  "download:reports",
  "create:equipment",
  "update:equipment",
  "delete:equipment",
  "create:intervention-categories",
  "update:intervention-categories",
  "delete:intervention-categories",
  "create:interventions",
  "update:interventions",
  "delete:interventions",
  // "download:audit-files",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type NavIconKey =
  | "dashboard"
  | "clean-room"
  | "history"
  | "audit-trail"
  | "users"
  | "roles"
  | "masters"
  | "reports";

export type RouteAccessRule = {
  /** Route prefix, e.g. `/dashboard` (no trailing slash). */
  path: string;
  /** Required permission; omit to allow any authenticated user. */
  permission?: Permission;
  nav?: {
    title: string;
    icon: NavIconKey;
    /** Lower numbers appear first in the sidebar. */
    order: number;
    section?: "platform" | "settings";
    /** Extra path prefixes that should highlight this nav item. */
    activePaths?: string[];
  };
};

/** Route → permission (+ optional sidebar). Order does not affect matching. */
export const ROUTE_RULES: readonly RouteAccessRule[] = [
  {
    path: "/dashboard",
    permission: "view:dashboard",
    nav: { title: "Dashboard", icon: "dashboard", order: 10, section: "platform" },
  },
  {
    path: "/masters",
    permission: "view:masters",
    nav: {
      title: "Master",
      icon: "masters",
      order: 40,
      section: "settings",
      activePaths: ["/equipment", "/intervention-categories"],
    },
  },
  {
    path: "/studies",
    permission: "view:history",
    nav: {
      title: "Studies",
      icon: "history",
      order: 20,
      section: "platform",
      activePaths: ["/frame"],
    },
  },
  // {
  //   path: "/clean-room",
  //   permission: "upload:videos",
  //   nav: { title: "Clean Room", icon: "clean-room", order: 20 },
  // },
  {
    path: "/reports",
    permission: "view:reports",
    nav: { title: "Reports", icon: "reports", order: 30, section: "platform" },
  },
  {
    path: "/logs",
    permission: "view:audit-trail",
    nav: { title: "Logs", icon: "audit-trail", order: 50, section: "settings" },
  },
  {
    path: "/services",
  },
  // {
  //   path: "/users",
  //   permission: "view:users",
  //   nav: { title: "Users", icon: "users", order: 50 },
  // },
  {
    path: "/roles",
    permission: "view:roles",
    nav: { title: "Roles", icon: "roles", order: 60, section: "settings" },
  },
  { path: "/equipment", permission: "view:masters" },
  { path: "/intervention-categories", permission: "view:masters" },
  { path: "/frame", permission: "view:alerts" },
  { path: "/screenshot", permission: "view:screenshots" },
  { path: "/profile" },
];

/** Preferred post-login paths when the user cannot access the current route. */
export const LANDING_PATH_PRIORITY = [
  "/dashboard/",
  "/reports/",
  "/studies/",
  "/masters/",
  "/logs/",
  "/roles/",
] as const;

/** Maps API / legacy role strings to `AppRole`. */
export const ROLE_ALIASES: Record<string, AppRole> = {
  ADMIN: "ADMIN",
  ADMINISTRATOR: "ADMIN",
  VIEWER: "VIEWER",
  REVIEWER: "REVIEWER",
  AUDITOR: "AUDITOR",
};
